import { getDataOnRealtimeDb, saveDataOnRealtimeDb } from '../utils/firebase';
import { ExpenseCategory, IncomeSource, BillCategory, currencyOptions, BudgetCategory } from '../utils/Catogory';

class UserInitializationService {
    constructor(userId) {
        this.userId = userId;
        this.basePath = `users/${userId}`;
    }

    async initializeUser() {
        try {
            const initialized = await this.checkInitialization();
            if (initialized) {
                console.log('User already initialized');
                return;
            }

            // Sequential initialization to ensure all data is properly saved
            await this.initializeExpenseCategories();
            await this.initializeIncomeCategories();
            await this.initializeBillCategories();
            await this.initializeBudgetCategories();
            await this.initializeCurrencyOptions();
            await this.initializeSettings();

            await this.markAsInitialized();
            console.log('User initialization complete');
        } catch (error) {
            console.error('Error during user initialization:', error);
            throw error;
        }
    }

    async checkInitialization() {
        const initStatus = await getDataOnRealtimeDb(`${this.basePath}/initialized`);
        return !!initStatus;
    }

    async initializeExpenseCategories() {
        try {
            const path = `${this.basePath}/category/expense`;
            const existing = await getDataOnRealtimeDb(path);

            if (!existing) {
                await saveDataOnRealtimeDb(path, ExpenseCategory);
                console.log('Expense categories initialized');
            }
        } catch (error) {
            console.error('Error initializing expense categories:', error);
            throw error;
        }
    }

    async initializeIncomeCategories() {
        try {
            const path = `${this.basePath}/category/income`;
            const existing = await getDataOnRealtimeDb(path);

            if (!existing) {
                await saveDataOnRealtimeDb(path, IncomeSource);
                console.log('Income sources initialized');
            }
        } catch (error) {
            console.error('Error initializing income categories:', error);
            throw error;
        }
    }

    async initializeBillCategories() {
        try {
            const path = `${this.basePath}/category/bill`;
            const existing = await getDataOnRealtimeDb(path);

            if (!existing) {
                await saveDataOnRealtimeDb(path, BillCategory);
                console.log('Bill categories initialized');
            }
        } catch (error) {
            console.error('Error initializing bill categories:', error);
            throw error;
        }
    }

    async initializeBudgetCategories() {
        try {
            const path = `${this.basePath}/category/budget`;
            const existing = await getDataOnRealtimeDb(path);

            if (!existing) {
                await saveDataOnRealtimeDb(path, BudgetCategory);
                console.log('Budget categories initialized');
            }
        } catch (error) {
            console.error('Error initializing budget categories:', error);
            throw error;
        }
    }

    async initializeCurrencyOptions() {
        const path = `${this.basePath}/currencies`;
        const existing = await getDataOnRealtimeDb(path);

        if (!existing) {
            const currencies = currencyOptions.reduce((acc, currency) => {
                acc[currency.value] = {
                    value: currency.value,
                    label: currency.label,
                    symbol: currency.symbol
                };
                return acc;
            }, {});

            await saveDataOnRealtimeDb(path, currencies);
            console.log('Currency options initialized');
        }
    }

    async initializeSettings() {
        const path = `${this.basePath}/settings`;
        const existing = await getDataOnRealtimeDb(path);

        if (!existing) {
            const defaultSettings = {
                general: {
                    name: '',
                    phoneNumber: '',
                    currency: 'USD',
                    currencySymbol: '$',
                    language: 'en',
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                },
                notifications: {
                    email: {
                        enabled: true,
                        budgetAlerts: true,
                        billReminders: true,
                        goalProgress: true,
                        weeklyReport: true
                    },
                    frequency: 'daily'
                },
                display: {
                    theme: 'light',
                    dateFormat: 'MM/DD/YYYY',
                    numberFormat: {
                        decimals: 2,
                        thousandsSeparator: ',',
                        decimalSeparator: '.'
                    }
                }
            };
            await saveDataOnRealtimeDb(path, defaultSettings);
            console.log('Settings initialized');
        }
    }

    async markAsInitialized() {
        await saveDataOnRealtimeDb(`${this.basePath}/initialized`, {
            timestamp: new Date().toISOString(),
            status: true
        });
    }
}

const createUserInitService = (userId) => new UserInitializationService(userId);

export default createUserInitService; 